import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getBlogPosts, BlogPost } from '../services/api';
import { getCurrentDomain } from '../config/domains';
import { useAuth } from '../context/AuthContext';

const PAGE_SIZE = 20;

const Blog: React.FC = () => {
  const domain = getCurrentDomain();
  const { isBlogger } = useAuth();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = useCallback(async (offset: number, append: boolean) => {
    if (append) setLoadingMore(true); else setLoading(true);
    const response = await getBlogPosts(PAGE_SIZE, offset);
    if (response.error) {
      setError(response.error);
    } else if (response.data) {
      setPosts(prev => append ? [...prev, ...(response.data?.posts || [])] : (response.data?.posts || []));
      setTotal(response.data.total);
    }
    if (append) setLoadingMore(false); else setLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts(0, false);
  }, [fetchPosts]);

  if (!domain.hasBlog) {
    return (
      <div>
        <h1 className="page-title">Blog</h1>
        <p>Blog is not available for this site.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading blog posts...</div>;
  }

  if (error) {
    return <div className="error">Error loading blog posts: {error}</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <p className="page-content" style={{ fontSize: '1.1rem', marginBottom: '0' }}>
          Insights, tutorials, and updates from the Intrnauts team.
        </p>
      </div>

      {isBlogger && (
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '15px' }}>Blog Management</h2>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <Link
              to="/admin/blog"
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                transition: 'opacity 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              Manage Blog Posts
            </Link>
            <Link
              to="/admin/blog/new"
              style={{
                padding: '12px 24px',
                backgroundColor: '#28a745',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                transition: 'opacity 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              Create New Post
            </Link>
          </div>
        </div>
      )}

      <div className="blog-list">
        {posts.length === 0 ? (
          <p>No blog posts yet. Check back soon!</p>
        ) : (
          posts.map((post) => (
            <article key={post.id} className="blog-post-card">
              <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                <h2 className="blog-post-title" style={{ color: domain.primaryColor }}>
                  {post.title}
                </h2>
              </Link>
              <div className="blog-post-meta">
                By {post.author} on {new Date(post.published_date).toLocaleDateString()}
              </div>
              <p className="blog-post-excerpt">{post.excerpt}</p>
              {post.tags && post.tags.length > 0 && (
                <div className="blog-tags">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/blog/tag/${encodeURIComponent(tag)}`}
                      className="blog-tag blog-tag-link"
                      style={{ textDecoration: 'none' }}
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              )}
            </article>
          ))
        )}
      </div>

      {posts.length < total && (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            className="btn btn-secondary"
            onClick={() => fetchPosts(posts.length, true)}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : `Load more (${total - posts.length} remaining)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default Blog;
